import { UserContext, VibratoClientContext } from "@/components/dashboard/contexts"
import ReactGA from "react-ga4"
import ReactPixel from "react-facebook-pixel"
import { Modal, ModalBody } from "flowbite-react"
import { BrightStar, Phone, Star } from "iconoir-react" // Added Xmark for close button
import mixpanel from "mixpanel-browser"
import React, { useContext, useEffect, useRef, useState } from "react"
import { Link } from "react-router-dom"
import { settings } from "@/settings"
import { IosTrackingContext } from "../GlobalLayout"

const PLAN_FEATURES: Record<string, { name: string; features: { text: string; icon: string; color: string }[] }> = {
  freemium: {
    name: "Freemium",
    features: [
      { text: "30 minutes of free call time to try Vibrato", icon: "💰", color: "blue" },
      { text: "Add up to 10 contacts", icon: "👥", color: "gray" },
      { text: "1 customizable task template", icon: "📋", color: "gray" },
      { text: "1 test campaign", icon: "📣", color: "gray" },
      { text: "Basic feature access", icon: "🔧", color: "gray" }
    ]
  },
  lite: {
    name: "Vibrato Lite",
    features: [
      { text: "20 minutes of call time per month (then $0.25/min)", icon: "💰", color: "blue" },
      { text: "1 custom phone number", icon: "📞", color: "green" },
      { text: "Complete access to chatbot and task library", icon: "🤖", color: "green" },
      { text: "Save up to 3 calls as task templates", icon: "📋", color: "red" },
      { text: "Store up to 20 contact profiles", icon: "👥", color: "gray" }
    ]
  },
  plus: {
    name: "Vibrato Plus",
    features: [
      { text: "Unlimited non-commercial call minutes", icon: "💰", color: "blue" },
      { text: "AI voice cloning included", icon: "🧬", color: "green" },
      { text: "1 campaign with up to 50 contacts", icon: "📣", color: "red" },
      { text: "Up to 10 task templates", icon: "📋", color: "gray" },
      { text: "Full API integration access", icon: "🔗", color: "gray" },
      { text: "1 custom phone number", icon: "📞", color: "green" },
      { text: "Priority call processing", icon: "⚡", color: "green" }
    ]
  },
  standard: {
    name: "Vibrato Standard",
    features: [
      { text: "50 minutes of call time per month", icon: "📞", color: "blue" },
      { text: "1 custom phone number", icon: "📞", color: "green" },
      { text: "Additional minutes at $0.20/min", icon: "💵", color: "green" },
      { text: "Up to 4 task templates", icon: "📋", color: "red" },
      { text: "Manage up to 35 contacts", icon: "👥", color: "gray" },
      { text: "AI voice cloning included", icon: "🎤", color: "gray" },
      { text: "API access", icon: "🔗", color: "gray" }
    ]
  },
  pro: {
    name: "Vibrato for Business - Pro",
    features: [
      { text: "450 minutes of call time per month", icon: "📞", color: "blue" },
      { text: "Additional minutes at $0.11/min", icon: "💵", color: "green" },
      { text: "Up to 20 task templates", icon: "📋", color: "red" },
      { text: "Manage up to 200 contacts", icon: "👥", color: "gray" },
      { text: "Run up to 5 campaigns", icon: "📣", color: "gray" },
      { text: "1 custom phone number", icon: "📞", color: "green" },
      { text: "AI voice cloning included", icon: "🧬", color: "green" },
      { text: "API access", icon: "🔗", color: "gray" },
      { text: "Commercial use allowed", icon: "💼", color: "green" },
      { text: "Priority call processing", icon: "⚡", color: "green" }
    ]
  },
  scale: {
    name: "Vibrato for Business - Scale",
    features: [
      { text: "2,000 minutes of call time per month", icon: "📞", color: "blue" },
      { text: "Best rate: $0.10/min after included minutes", icon: "💵", color: "green" },
      { text: "Priority support included", icon: "💼", color: "red" },
      { text: "Up to 30 task templates", icon: "📋", color: "gray" },
      { text: "Manage up to 2,000 contacts", icon: "👥", color: "gray" },
      { text: "Run up to 10 campaigns", icon: "📣", color: "gray" },
      { text: "2 concurrent calls allowed", icon: "📞📞", color: "red" },
      { text: "Up to 2 custom phone numbers", icon: "📞", color: "green" },
      { text: "AI voice cloning included", icon: "🧬", color: "green" },
      { text: "API access", icon: "🔗", color: "gray" },
      { text: "Commercial use allowed", icon: "💼", color: "green" }
    ]
  },
  enterprise: {
    name: "Vibrato for Business - Enterprise",
    features: []
  }
}

export default function SubscriptionSuccessModal(): React.JSX.Element {
  const vibratoClient = useContext(VibratoClientContext)
  const [openModal, setOpenModal] = useState(true)
  const user = useContext(UserContext)
  const [planSlug, setPlanSlug] = useState<string | null>(null)
  const [subscriptionProvider, setSubscriptionProvider] = useState<string | null>(null)
  const [subscription, setSubscription] = useState<any | null>(null)
  const [planName, setPlanName] = useState<string | null>(null)
  const [planFeatures, setPlanFeatures] = useState<{ text: string; icon: string; color: string }[] | null>(null)
  const [hasPhoneNumber, setHasPhoneNumber] = useState(false)
  const { iosAppTrackingEnabled } = useContext(IosTrackingContext)
  const modalBodyRef = useRef<HTMLDivElement>(null)

  // Scroll to top when modal opens or content changes
  const scrollToTop = () => {
    if (modalBodyRef.current) {
      modalBodyRef.current.scrollTop = 0
    }
  }

  useEffect(() => {
    if (openModal) {
      // Initial scroll to top
      scrollToTop()
      // Add a small delay to ensure content is rendered
      const timer = setTimeout(scrollToTop, 100)
      return () => clearTimeout(timer)
    }
  }, [openModal])

  // Scroll to top when content is loaded
  useEffect(() => {
    if (planFeatures && planName) {
      // Small delay to ensure all content is rendered
      const timer = setTimeout(scrollToTop, 50)
      return () => clearTimeout(timer)
    }
  }, [planFeatures, planName])

  useEffect(() => {
    async function setupSubscription() {
      if (!user) {
        return
      }

      const currentPlanSlug = user.plan
      let provider = null
      if (user.subscription_provider) {
        console.log("Subscription provider found for user", user.subscription_provider)
        setSubscriptionProvider(user.subscription_provider)
        provider = user.subscription_provider
      } else {
        console.error("No subscription provider found for user", user)
        return
      }

      if (openModal && currentPlanSlug && PLAN_FEATURES[currentPlanSlug]) {
        setPlanName(PLAN_FEATURES[currentPlanSlug].name)
        setPlanFeatures(PLAN_FEATURES[currentPlanSlug].features)
        setPlanSlug(currentPlanSlug)

        // Get active subscription
        const subscription =
          provider === "stripe" ? await vibratoClient.getSubscription() : await vibratoClient.getAppleSubscription()

        setSubscription(subscription)

        // Check if user has any phone numbers
        const phoneNumbers = await vibratoClient.getTwilioPhoneNumbers()
        setHasPhoneNumber(phoneNumbers.length > 0)
      }
    }

    setupSubscription()
  }, [openModal, user, user.subscription_provider, user.plan, vibratoClient])

  useEffect(() => {
    const invoiceAmount = subscription?.last_invoice_amount || "unknown"

    if (planSlug && subscription) {
      mixpanel.track("Stripe Subscription Created (frontend)", {
        subscription_id: subscription?.id,
        subscription_provider: subscriptionProvider,
        plan: planSlug,
        amount: invoiceAmount
      })

      if (settings.IS_IOS && !iosAppTrackingEnabled) {
        return
      }

      // Track Google Analytics purchase
      ReactGA.event("purchase", {
        transaction_id: subscription?.id,
        value: invoiceAmount,
        currency: "USD",
        items: [
          {
            item_id: `vibrato_subscription_${planSlug}`,
            item_name: `Vibrato Subscription ${planSlug}`,
            affiliation: "Vibrato",
            quantity: 1,
            price: invoiceAmount,
            item_category: "Subscription"
          }
        ]
      })

      // Track Facebook Pixel purchase event
      ReactPixel.track("Purchase", {
        value: invoiceAmount,
        currency: "USD",
        content_type: "subscription",
        content_ids: [`vibrato_subscription_${planSlug}`],
        content_name: `Vibrato Subscription ${planSlug}`
      })
    }
  }, [planSlug, subscription])

  const handleClose = () => {
    setOpenModal(false)
  }

  return (
    <>
      <Modal show={openModal} onClose={handleClose} size="4xl" dismissible className="pt-safe" popup>
        <ModalBody ref={modalBodyRef} className="p-0 max-h-[90vh] overflow-y-auto">
          <div className="flex flex-col">
            {/* Header Section with Gradient Background */}
            <div className="bg-gradient-to-br from-purple-600 via-purple-700 to-indigo-600 text-white px-6 py-8 sm:px-8 sm:py-10 rounded-t-lg">
              <div className="flex items-center justify-center mb-6">
                <div className="bg-white/20 backdrop-blur-sm p-4 rounded-full shadow-lg">
                  <BrightStar className="w-8 h-8 sm:w-10 sm:h-10" />
                </div>
              </div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-center mb-3 leading-tight">
                Welcome to {planName}! 🎉
              </h2>
              <p className="text-center text-white/90 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
                Your journey to effortless communication begins now
              </p>
            </div>

            {/* Content Section */}
            <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-8">
              <div className="text-base sm:text-lg text-gray-700 mb-6 text-center sm:text-left">
                Your subscription to <span className="font-bold text-purple-600">{planName}</span> is now active!
              </div>

              {planFeatures && (
                <div className="bg-gradient-to-br from-gray-50 to-purple-50/30 rounded-2xl p-4 sm:p-6 mb-6 sm:mb-8 border border-gray-100 shadow-sm">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-4 sm:mb-6 text-center sm:text-left">
                    Your Premium Features <Star className="inline w-5 h-5 text-yellow-400 ml-1" />
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                    {planFeatures.map((feature, index) => (
                      <div
                        key={index}
                        className="flex items-center p-3 sm:p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-all duration-200 min-h-[70px] sm:min-h-[80px]"
                      >
                        <span className="text-xl sm:text-2xl mr-3 flex-shrink-0">{feature.icon}</span>
                        <span className={`font-medium text-sm sm:text-base text-${feature.color}-600 leading-snug`}>
                          {feature.text}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {!hasPhoneNumber && (
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-purple-100 mb-6">
                  <div className="max-w-2xl mx-auto text-center">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4 leading-tight">
                      Ready to Take Control of Your Calls? 🎯
                    </h3>
                    <p className="text-gray-600 mb-6 text-sm sm:text-base leading-relaxed">
                      Set up your own dedicated phone number to get a professional caller ID, receive callbacks, and
                      forward calls to your personal number. Perfect for managing communications while keeping your
                      personal number private.
                    </p>
                    <Link
                      to="/phone-numbers?add=true"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 sm:px-6 sm:py-3 bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white text-sm sm:text-base font-semibold rounded-full transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl"
                    >
                      <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                      Set Up Your Phone Number
                    </Link>
                    <p className="text-xs sm:text-sm text-purple-600 mt-3 sm:mt-4 font-medium">
                      Quick setup with full control over your calls
                    </p>
                  </div>
                </div>
              )}
            </div>

            {/* Footer with Close Button */}
            <div className="border-t border-gray-100 bg-gray-50/50 px-4 py-4 sm:px-6 sm:py-6 rounded-b-lg">
              <div className="flex justify-center">
                <button
                  onClick={handleClose}
                  className="inline-flex items-center justify-center px-4 py-2 sm:px-6 sm:py-2.5 bg-gray-600 hover:bg-gray-700 text-white text-sm font-medium rounded-full transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}
